import config from '@/config/index';
import { SS_SHARE_CHANNEL_ACCESS_TOKEN_KEY, THIRD_PARTY_SSO_K_I } from '@/constants/common';
import Base64 from 'js-base64';
import { Message } from 'view-design';
import VueRouter from 'vue-router';
import { debounce } from 'lodash';
import md5 from 'md5';

export const forEach = (arr: any, fn: any) => {
    if (!arr.length || !fn) {
        return;
    }
    let i = 0;
    const len = arr.length;
    while (i < len) {
        const item = arr[i];
        fn(item, i, arr);
        i += 1;
    }
};

/**
 * @param {Array} target 目标数组
 * @param {Array} arr 需要查询的数组
 * @description 判断要查询的数组是否至少有一个元素包含在目标数组中
 */
export const hasOneOf = (
    targetArr: any[],
    arr: any[],
) => targetArr.some((_: any) => arr.indexOf(_) > -1);

export const keyLighten = (
    str: string,
    search: string,
) => {
    const key = `(${search.replace(/([+.*|?\-([^$])/g, '\\$1').replace(/\s+/g, '|')})`;
    const match = new RegExp(key, 'igm');
    return str.replace(match, '<em class="key-light-text">$1</em>');
};

export const typeOf = (obj: any) => {
    const { toString } = Object.prototype;
    const map: any = {
        '[object Boolean]': 'boolean',
        '[object Number]': 'number',
        '[object String]': 'string',
        '[object Function]': 'function',
        '[object Array]': 'array',
        '[object Date]': 'date',
        '[object RegExp]': 'regExp',
        '[object Undefined]': 'undefined',
        '[object Null]': 'null',
        '[object Object]': 'object',
    };
    return map[toString.call(obj)];
};

// scrollTop animation
export const scrollTop = (
    el: any,
    from: number = 0,
    to: number,
    duration: number = 500,
    endCallback?: any,
) => {
    const func = (callback: any) => window.setTimeout(callback, 1000 / 60);
    if (!window.requestAnimationFrame) {
        window.requestAnimationFrame = (
            window.webkitRequestAnimationFrame ||
            window.mozRequestAnimationFrame ||
            window.msRequestAnimationFrame ||
            func
        );
    }
    const difference = Math.abs(from - to);
    const stepMath = Math.ceil(difference / (duration * 50));
    const $el: any = el;

    function scroll(start: number, end: number, step: number) {
        if (start === end) {
            if (endCallback) {
                endCallback();
            }
            return;
        }

        let d = (start + step > end) ? end : start + step;
        if (start > end) {
            d = (start - step < end) ? end : start - step;
        }

        if (el === window) {
            window.scrollTo(d, d);
        } else {
            $el.scrollTop = d;
        }
        window.requestAnimationFrame(() => scroll(d, end, step));
    }
    scroll(from, to, stepMath);
};

// fn 是我们需要包装的事件回调, delay 是时间间隔的阈值
export const throttle = (fn: any, delay: number): any => {
    // last 为上一次触发回调的时间
    let last = 0;
    // console.log('delay: ', delay);

    // 将 throttle 处理结果当作函数返回
    return (...args: any[]) => {
        // 保留调用时的 this 上下文
        // console.log('args: ', args);
        const context = this;
        // 保留调用时传入的参数
        const newArgs = args;
        // 记录本次触发回调的时间
        const now = +new Date();
        // 判断上次触发的时间和本次触发的时间差是否小于时间间隔的阈值
        if (now - last >= delay) {
            // 如果时间间隔大于我们设定的时间间隔阈值，则执行回调
            last = now;
            fn.apply(context, newArgs);
        }
    };
};

// // fn是我们需要包装的事件回调, delay是时间间隔的阈值
// export const throttle = (fn: Function, delay: number): any => {
//     // last为上一次触发回调的时间, timer是定时器
//     let last = 0;
//     let timer: any = null;
//     // console.log('delay: ', delay);
//     // 将throttle处理结果当作函数返回
//     // eslint-disable-next-line
//     return function () {
//         // 保留调用时的this上下文
//         const context = this;
//         // 保留调用时传入的参数
//         // eslint-disable-next-line
//         const args = arguments;
//         // console.log('args: ', args);
//         // 记录本次触发回调的时间
//         const now = +(new Date()).getTime();
//         // console.log('now: ', now);
//         // console.log('last: ', last);
//         // console.log('now - last: ', now - last);
//         // console.log('now - last < delay: ', now - last < delay);
//         // 判断上次触发的时间和本次触发的时间差是否小于时间间隔的阈值
//         if (now - last < delay) {
//             // 如果时间间隔小于我们设定的时间间隔阈值，则为本次触发操作设立一个新的定时器
//             clearTimeout(timer);
//             timer = setTimeout(() => {
//                 last = now;
//                 fn.apply(context, args);
//             }, delay);
//         } else {
//             // 如果时间间隔超出了我们设定的时间间隔阈值，那就不等了，无论如何要反馈给用户一次响应
//             last = now;
//             fn.apply(context, args);
//         }
//     };
// };

export const isElectron = () => window.navigator.userAgent.toLocaleLowerCase().indexOf('electron') !== -1;
export const isStaffVersion = () => window.navigator.userAgent.toLocaleLowerCase().indexOf('isstaff') !== -1 || !!window.localStorage.getItem('IS_STAFF');
export const getServerPort = (): number => {
    let port = 4437;
    if (isElectron()) {
        port = +(window.localStorage.getItem('SERVER_PORT') || 4435);
    }
    return port;
};
export const bin2hex = (s: string) => {
    let n = '';
    let o: any = '';
    s += '';
    for (let i = 0; i < s.length; i += 1) {
        n = s.charCodeAt(i).toString(16);
        o += n.length < 2 ? `0${n}` : n;
    }
    return o;
};

export const getCanvasFingerprint = (): string => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d') as any;
    const txt = 'adspower.net';
    ctx.textBaseline = 'top';
    ctx.font = '14px "Arial"';
    ctx.textBaseline = 'tencent';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText(txt, 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText(txt, 4, 17);

    const b64 = canvas.toDataURL().replace('data:image/png;base64,', '');
    const bin = atob(b64);
    const crc = bin2hex(bin.slice(-16, -12));
    return crc;
};
export const isAllowRequire = () => window.navigator.userAgent.toLocaleLowerCase().indexOf('adspower') !== -1;
export const isMac = () => /macintosh|mac os x/i.test(navigator.userAgent);
export const screenResolutionList = () => [
    {
        key: 'none',
        value: 'Default',
    }, {
        key: '800_600',
        value: '800 x 600',
    }, {
        key: '1024_600',
        value: '1024 x 600',
    }, {
        key: '1024_640',
        value: '1024 x 640',
    }, {
        key: '1024_768',
        value: '1024 x 768',
    }, {
        key: '1152_864',
        value: '1152 x 864',
    }, {
        key: '1280_720',
        value: '1280 x 720',
    }, {
        key: '1280_768',
        value: '1280 x 768',
    }, {
        key: '1280_800',
        value: '1280 x 800',
    }, {
        key: '1280_960',
        value: '1280 x 960',
    }, {
        key: '1280_1024',
        value: '1280 x 1024',
    }, {
        key: '1360_768',
        value: '1360 x 768',
    }, {
        key: '1366_768',
        value: '1366 x 768',
    }, {
        key: '1400_1050',
        value: '1400 x 1050',
    }, {
        key: '1400_900',
        value: '1400 x 900',
    }, {
        key: '1536_864',
        value: '1536 x 864',
    }, {
        key: '1600_900',
        value: '1600 x 900',
    }, {
        key: '1600_1200',
        value: '1600 x 1200',
    }, {
        key: '1680_1050',
        value: '1680 x 1050',
    }, {
        key: '1920_1080',
        value: '1920 x 1080',
    }, {
        key: '1920_1200',
        value: '1920 x 1200',
    }, {
        key: '2048_1152',
        value: '2048 x 1152',
    }, {
        key: '2304_1440',
        value: '2304 x 1440',
    }, {
        key: '2560_1440',
        value: '2560 x 1440',
    }, {
        key: '2560_1600',
        value: '2560 x 1600',
    }, {
        key: '2880_1800',
        value: '2880 x 1800',
    }, {
        key: '4096_2304',
        value: '4096 x 2304',
    }, {
        key: '5120_2880',
        value: '5120 x 2880',
    },
];
export const checkCustomizedVersion = () => {
    return config.siteConfig.brand;
};

const shareVerifyList = ['shareVerify', 'shareVerifyChannel', 'shareVerifyCustomer'];

export const jumpShareVerifyPageMap: Record<string, string> = {
    shareVerify: 'shareVerifyChannel',
    shareLinkChannelAnalysis: 'shareVerifyChannel',
    shareLinkCustomerAnalysis: 'shareVerifyCustomer',
};

/**
 * 分享链接过期跳转回验证页
 * @param router
 * @param code
 */
const exitShare = (router: VueRouter, code: number, name: string) => {
    const { name: currentName, params, query } = router.app.$route;
    const jumpName = jumpShareVerifyPageMap[name] || name;

    console.log(currentName, jumpName);

    if (!shareVerifyList.includes(currentName as string)) {
        router.replace({
            name: jumpName,
            params: {
                id: params.shareId || '',
            },
        });
    }
    Message.error({
        content: router.app.$t(`errorCode.${code}`),
        duration: 6,
    });
};

export const debounceExitShare = debounce(exitShare, 1000);

/**
 * 生成签名
 * @param {Record<string, any>} params - 请求参数对象
 * @param {string} secret - 密钥
 * @param {string} method - 请求类型
 * @returns {string} 生成的签名字符串
 */
export function genSign(params: any = {}, secret: string, method = 'get') {
    params = JSON.parse(JSON.stringify(params));
    let newStr = '';
    Object.keys(params).sort().forEach((k) => {
        if (k) {
            // if (method === 'get' || !method) {
            //     newStr += `${k}=${params[k]}&`;
            // } else {
            //     newStr += `${encodeURIComponent(k)}=${encodeURIComponent(
            //         params[k],
            //     )}&`;
            // }
            newStr += `${k}=${params[k]}&`;
        }
    });
    newStr = newStr.slice(0, -1);
    const sign = md5(`${secret}&${newStr}`);
    return sign;
}

/**
 * 处理申请企业认证点击事件
 * @param e 点击事件
 */
export const handleInputContactHumanClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('ss_input_contact_human')) {
        if (window.ssq) {
            window.ssq.push('chatOpen');
            setTimeout(() => {
                window.ssq.push('sendTextMessage', '申请企业认证');
            }, 500);
        }
    }

    if (target.classList.contains('ss_input_contact_business')) {
        const left = ((window.innerWidth - 820) / 2) + window.screenLeft;
        const top = ((window.innerHeight - 640) / 2) + window.screenTop;
        window.open('https://chat.salesmartly.com/service/f11lv7v', '_blank', `width=820,height=640,left=${left},top=${top}`);
    }
};
